<template>
<div class="with-code-wrapper">
  <img class="bg-img" v-if="type === 'login' && showBg" :src="$cvWebpFn('https://img.kilimall.com/c/h5/login_bg.png', { w: 600 })" v-webp alt="">

  <div 
    :class="
      type === 'login' 
        ? 'with-password-form login-form'
        : 'with-password-form'"
    >
    <van-tabs
      v-if="(bandingType !== 'phone' && bandingType !== 'email') || type === 'binding'"
      v-model:active="tabAct"
      animated
      class="tab-div"
      @change="(n) => { tabAct = n}"
    >
      <van-tab v-if="type === 'login'" name="whatsapp">
        <template #title>
          <div class="tab-title">
            <img
              class="tab-icon"
              :src="$cvWebpFn(tabAct === 'whatsapp' ? TAB_ICON.whatsappSel : TAB_ICON.whatsappUnsel)"
              alt=""
            >
            <span>WhatsApp</span>
          </div>
        </template>
      </van-tab>
      <van-tab name="phone">
        <template #title>
          <div class="tab-title">
            <img
              v-if="type === 'login'"
              class="tab-icon"
              :src="$cvWebpFn(tabAct === 'phone' ? TAB_ICON.phoneSel : TAB_ICON.phoneUnsel)"
              alt=""
            >
            <span>{{ type !== 'login' ? 'Bind Phone No.' : 'Phone No.' }}</span>
          </div>
        </template>
      </van-tab>
      <van-tab name="email">
        <template #title>
          <div class="tab-title">
            <van-icon v-if="type === 'login'" name="envelop-o" size="20" />
            <span>{{ type !== 'login' ? 'Bind Email' : 'Email' }}</span>
          </div>
        </template>
      </van-tab>
    </van-tabs>
    
    <van-form @submit="onSubmit" class="inner-form" :class="{'user-info' : type !== 'binding' && type !== 'login'}">
      <van-field
        v-model.trim="loginForm.account"
        name="account"
        :placeholder="accountPlaceholder"
        :rules="[
          { required: true, message: 'Please input account' },
          { 
            validator: validateAccount,
            message: isPhoneTab
              ? 'Phone number format is incorrect.'
              : 'E-mail format is incorrect.'
          }
        ]"
        @click-left-icon="handleOpenSwitchRegionClick"
      >
        <template #left-icon>
          <div class="region-prefix" v-show="isPhoneTab">
            <img 
              class="region-icon" 
              v-lazy="currentCountry?.flag" 
              v-webp
              alt=""
            >
            <span class="prefix">+{{ currentCountry?.callingCode }}</span>
            <van-icon name="arrow-down" size="14" />
          </div>
        </template>
      </van-field>
      <van-field
        v-model.trim="loginForm.code"
        name="code"
        :placeholder="tabAct === 'whatsapp' ? 'WhatsApp code' : 'Please input code'"
        :rules="[{ required: true, message: 'Please input code' }]"
        @click-right-icon="handleSendCodeClick"
      >
        <template #right-icon>
          <span v-if="countdown >= 60" class="send-code-btn" id="getVerifyCode">Send</span>
          <span v-else>{{ countdown }}s</span>
        </template>
      </van-field>

      <div class="submit-button">
        <van-button block type="primary" native-type="submit">
          {{ submitText }}
        </van-button>
        <van-button
          v-if="type !== 'binding' && type !== 'login'" 
          type="default"
          round
          class="skip-btn"
          @click="skipClickFn"
        >
          Skip
        </van-button>
      </div>
    </van-form>
  </div>
  <div v-if="type !== 'binding' && type !== 'login'" class="text-div">
    <h3 class="title">Your {{ tabAct === 'email' ? 'email' : 'phone number' }} will be used for:</h3>
    <ul>
      <li class="red">Account security (e.g., verification codes)</li>
      <li class="red">Order updates and delivery notifications</li>
      <li>Personalized offers and discounts</li>
    </ul>
    <p>
      We promise not to share your phone number with third parties for marketing purposes, and we will take strict security measures to protect your information.
    </p>
  </div>
</div>
<KSwitchCountry 
  v-model:show="switchCountryPopup"
  :type="type"
  @country:click="handleSwitchCountryClick"
/>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { 
  type ISendCode,
  type ICodeLogin,
  sendCode, sendEmailCode, sendWhatsAppCode,
  loginByCode, loginByEmail, loginByWhatsApp
} from '@/api/models/system'
import { showToast } from 'vant'
import { postAccountPhone, postAccountEmail } from '@/api/models/userInfo'
import { webClickFn, customEventFn, eventName } from '@/plugins/bi-sa.client'
import { resolveCurrentRegion } from '@/utils/region'

interface IProps {
  type?: WithCodeType
  submitText?: string,
  bandingType?: string // emall | phone | all
  showBg?: boolean
}
export interface ILoginCodeForm {
  PfxRegion: number | string
  account: string
  code: string
  sn: string,
  phoneRegionId?: number | string
}
const props = withDefaults(
  defineProps<IProps>(),
  {
    type: 'login',
    submitText: 'Submit',
    showBg: false
  }
)
const emits = defineEmits<{
  (event: 'submit', form: ICodeLogin): void,
  (event: 'bindCallBack'): void
}>()
const loginForm = reactive<ILoginCodeForm>({
  PfxRegion:'',
  account: '',
  code: '',
  sn: '',
  phoneRegionId: ''
})

const countdown = ref<number>(60)
const interval = ref<NodeJS.Timer | null>()
const { $setUserInfo, $sensorsTrack, $SENSORS_EVENT, $registerSuccessTrack } = useNuxtApp()
const route = useRoute()
// 国家处理
const countrys = computed(() => useCountrys().value || [])
const userSelectedCountry = ref(false)
const currentCountry = ref<region.IRegionItem | undefined>(resolveCurrentRegion(countrys.value))

const syncDefaultCountry = () => {
  if (userSelectedCountry.value) {
    return
  }
  const resolved = resolveCurrentRegion(countrys.value)
  if (resolved) {
    currentCountry.value = resolved
  }
}

watch(countrys, syncDefaultCountry, { immediate: true })
watch([() => useCurrentCountry().value, () => useCurrentCountryCode().value], syncDefaultCountry)
// 切换国家 Popup
const switchCountryPopup = ref<boolean>(false)
const tabAct = ref<string>(props.type === 'login' ? 'whatsapp' : 'phone')

const TAB_ICON = {
  whatsappSel: 'https://image.kilimall.com/c/h5/login/ic_whats_app_sel.png',
  whatsappUnsel: 'https://image.kilimall.com/c/h5/login/ic_whats_app_unsel.png',
  phoneSel: 'https://image.kilimall.com/c/h5/login/ic_phone_no_sel.png',
  phoneUnsel: 'https://image.kilimall.com/c/h5/login/ic_phone_no_unsel.png'
}

const isPhoneTab = computed(() => tabAct.value === 'phone' || tabAct.value === 'whatsapp')
const accountPlaceholder = computed(() => {
  if (tabAct.value === 'whatsapp') return 'WhatsApp Number'
  if (tabAct.value === 'phone') return 'Phone No.'
  return 'Email Address'
})

if (props.bandingType === 'email') {
  // 老用户通过邮箱绑定
  tabAct.value = 'email'
}

const resetLoginForm = () => {
  resetInterval()

  loginForm.sn = ''
  loginForm.account = ''
  loginForm.code = ''
  userSelectedCountry.value = false
  syncDefaultCountry()

  // tabAct.value =  props.bandingType === 'email' ? 'email' : 'phone'
}

watch(() => tabAct.value, resetLoginForm)
defineExpose({ resetLoginForm, tabAct })

// 老用户绑定手机、邮箱
const oldBindFn = async (type: 'phone' | 'email') => {
  const userInfo: any = useCookie<IUserInfo>('UserInfo').value
  const { account, code, sn } = loginForm
  const params: any = {
    sn: sn,
    code: code
  }
  params[type] = account
  if (type === 'phone') {
    params.phoneRegionId = currentCountry.value?.id ?? useCurrentCountry().value
  }
  const postApi = type === 'phone'? postAccountPhone : postAccountEmail
  const res = await postApi(params)
  if(res && res.code === 0) {
    userInfo[type] = account
    useCookie<IUserInfo>('UserInfo').value = userInfo
    useUserInfo().value = userInfo
    emits('bindCallBack')
  }

}

const isEmail = (email: string):boolean => /^[\.a-zA-Z0-9_-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/.test(email)
const validateAccount = (val: string) => {
  if (isPhoneTab.value) {
    return /^[0-9]+$/.test(val)
  }
  return isEmail(val)
}
const skipClickFn = () => {
  emits('bindCallBack')
}
const handleSendCodeClick = async () => {
  if (!loginForm.account) {
    showToast('Please input account')
    return
  }

  if(interval.value) {
    return
  }
  interval.value = setInterval(() => {
    countdown.value -= 1

    if(countdown.value <= 0 && interval.value) {
      clearInterval(interval.value)
      interval.value = null
      countdown.value = 60
    }
  }, 1000)
  const { account } = loginForm
  const sendCodeParams: ISendCode = { type: 1 }
  // 老用户绑定手机号或邮箱 type 2
  if (props.bandingType === 'phone' || props.bandingType === 'email' || props.bandingType === 'all') {
    sendCodeParams.type = 2
  }
  let sendFn: (params: ISendCode) => Promise<any> = sendCode
  // 点击埋点
  let webClickIndex = 5

  if(tabAct.value === 'whatsapp' || tabAct.value === 'phone') {
    const rid = currentCountry.value?.id ?? useCurrentCountry().value
    sendCodeParams.PfxRegion = rid
    sendCodeParams.phoneRegionId = rid
    loginForm.PfxRegion = rid
    loginForm.phoneRegionId = rid
    sendCodeParams.phone = account

    if(!/^[0-9]+$/.test(account)) {
      resetInterval()
      return
    }

    if(tabAct.value === 'whatsapp') {
      sendFn = sendWhatsAppCode
      webClickIndex = 13
    }
  } else {
    if(!isEmail(account)) {
      resetInterval()
      return
    }
    sendCodeParams.email = account

    sendFn = sendEmailCode
    webClickIndex = 6
  }

  try {
    await sendFn(sendCodeParams).then((r: any) => {
      if (r.code === 0) {
        loginForm.sn = r.data.sn
        webClickFn(webClickIndex, 'Send', true)
      } else {
        webClickFn(webClickIndex, 'Send', false, 'api_code_error', r.msg || r.message)
        showToast(r.msg || r.message)
      }
    })
    
  } catch(e: any) {
    resetInterval()
    webClickFn(webClickIndex, 'Send', false, 'api_code_error', e.msg || e.message)
  }
}

const resetInterval = () => {
  if(!interval.value) {
    return
  }

  clearInterval(interval.value)
  interval.value = null
  countdown.value = 60
}
const handleOpenSwitchRegionClick = () => {
  if (!isPhoneTab.value) {
    return
  }
  switchCountryPopup.value = true
}
const handleSwitchCountryClick = (e: region.IRegionItem) => {
  userSelectedCountry.value = true
  currentCountry.value = e
}
const onSubmit = async() => {
  if(!loginForm.sn) {
    return showToast('Please send verification code.')
  }

  if (props.bandingType === 'phone' || props.bandingType === 'email' || props.bandingType === 'all') {
    await oldBindFn(tabAct.value as 'phone' | 'email')
    return
  }

  const { PfxRegion, account, code, sn, phoneRegionId } = loginForm

  const params: ICodeLogin = { code, sn }
  if(tabAct.value === 'phone' || tabAct.value === 'whatsapp') {
    params.phone = account
    params.PfxRegion = PfxRegion
    params.phoneRegionId = phoneRegionId || PfxRegion
  } else {
    params.email = account
  }

  const { referCode, referSource, referSourceId } = route.query

  referCode && (params.referCode = referCode as string)
  referSource && (params.referSource = referSource as string)
  referSourceId && (params.referSourceId = referSourceId as string)

  let loginFn = loginByEmail
  if(tabAct.value === 'phone') {
    loginFn = loginByCode
  } else if(tabAct.value === 'whatsapp') {
    loginFn = loginByWhatsApp
  }

  try {
    const userInfo = await loginFn(params)
    await $setUserInfo(userInfo, '', props.type === 'binding')
    
    $sensorsTrack($SENSORS_EVENT.LoginSuccess, { LoginMethod: tabAct.value})
    customEventFn(eventName.loginSuccess,  { login_method: tabAct.value})

    if(!userInfo.isExistAccount || userInfo.isRegister) {
      const registerMethod = tabAct.value === 'email'
        ? 'email'
        : tabAct.value === 'whatsapp'
          ? 'whatsapp'
          : 'phone'
      $registerSuccessTrack(registerMethod)
      customEventFn(eventName.registerSuccess,  {
        register_method: tabAct.value === 'email'
          ? 'emailCode'
          : tabAct.value === 'whatsapp'
            ? 'whatsappCode'
            : 'phoneCode',
        register_source: 'normal'
      })
    }
    
    // 登录成功保留一个事件
    emits('submit', params)
  } catch(e: any) {
    console.error(e)
  }
}
</script>

<style lang="scss" scoped>
.region-prefix{
  display: flex;
  align-items: center;
  color: #505050;
  font-size: 28px;
  cursor: pointer;
  .region-icon{
    width: 68px;
    height: 43px;
  }
  .prefix{
    padding: 0 10px;
  }
}
.send-code-btn{
  color: #E04646;
  font-size: 28px;
  font-weight: 400;
}
.with-code-wrapper{
  .bg-img{
    width: 100%;
  }
  .login-form{
    margin-top: 0;
  }
  .with-password-form{
    background-color: #fff;
    overflow: hidden;
    position: relative;
    padding: 0 40px 0px 40px;
  }
  .tab-title{
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .tab-icon{
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: block;
  }
  .user-info{
    margin-top: 20px;
    .van-cell {
      border: 2px solid #99999980;
      margin-bottom: 30px;
    }
  }
  .submit-button{
    margin-top: 40px;
    .van-button--primary {
      border-radius: 12px;
      overflow: hidden;
      font-size: 32px;
      font-weight: 700;
      background: #E04646;
      border-color: #E04646;
    }
  }
}
.text-div {
  padding: 0 40px;
  h3 {
    color: #666666;
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  ul {
    margin-top: 20px;
    font-size: 28px;
    font-weight: 400;
    list-style-type: disc;
    padding-left: 34px;
    li {
      line-height: 40px;
      font-style: normal;
      color: #999999;
    }
    li.red {
      color: var(--main_color);
    }
  }
  p {
    font-size: 28px;
    font-weight: 400;
    color: #999999;
    line-height: 40px;
    margin-bottom: 158px;
  }
}
.tab-div {
  margin-bottom: 40px;
  :deep(.van-tab__text--ellipsis) {
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
  }
  :deep(.van-tabs__line) {
    background: linear-gradient(270deg, #f3585803 -11.16%, #F35656 40%);
  }
  :deep(.van-tab--active) {
    color: #E04545;
  }
}
.skip-btn {
  border: 0px;
  width: 100%;
  color: #666666;
  font-size: 32px;
  margin-top: 10px;
}
</style>
